import { useEffect, useMemo, useState } from 'react'
import { Typeahead, Pill, Flex, Title, Icon, Body, Caption } from 'playbook-ui'
import { searchItems } from '../utils/searchItems'
import { useDarkMode } from '../contexts/DarkModeContext'

type Kit = {
  kitLabel?: string,
  label: string,
  path?: string,
  platforms?: string[],
  propName?: string,
  props?: KitProp[],
  searchTerms?: string[],
  value: string,
  type?: string,
}

type KitProp = {
  name: string,
  platforms: string[],
}

type KitSearchProps = {
  classname: string,
  kits: Kit[],
  id: string,
  platform?: string,
  global_props_and_tokens?: Record<string, any>,
  marginBottom?: string,
  onNavigate?: (path: string) => void,
  searchResetKey?: string,
}

const SEARCH_GROUP_ORDER: Record<string, number> = {
  kit: 0,
  global_prop: 1,
  global_event_prop: 1,
  prop: 2,
  token: 3,
}

const searchGroupRank = ({ type }: Kit): number => SEARCH_GROUP_ORDER[type ?? ''] ?? 0

const sortBySearchGroup = (items: Kit[]): Kit[] =>
  [...items].sort((a, b) => searchGroupRank(a) - searchGroupRank(b))

const stripPropSeparators = (value: string): string =>
  value.replace(/[_-]/g, '')

const normalizePropName = (name: string): string =>
  stripPropSeparators(name).toLowerCase()

const normalizedSearchTerms = (item: Kit): string[] =>
  (item.searchTerms ?? []).map(stripPropSeparators)

const PlatformMarks = ({ platforms }: { platforms: string[] }) => (
  <>
    {platforms.map((platform, index) => (
      <Icon
        color="primary"
        icon={platform}
        key={platform}
        marginRight={index === 0 && platforms.length === 2 ? 'xxs' : undefined}
        size="xs"
      />
    ))}
  </>
)

const kitPropItems = (kit: Kit): Kit[] => {
  const propsByName = new Map<string, { name: string, platforms: Set<string>, searchTerms: Set<string> }>()

  kit.props?.forEach(({ name, platforms }) => {
    const normalizedName = normalizePropName(name)
    const prop = propsByName.get(normalizedName) || {
      name,
      platforms: new Set<string>(),
      searchTerms: new Set<string>(),
    }
    const supportedPlatforms = platforms.length > 0 ? platforms : ['react', 'rails']

    supportedPlatforms.forEach((supportedPlatform) => prop.platforms.add(supportedPlatform))
    prop.searchTerms.add(name)
    propsByName.set(normalizedName, prop)
  })

  return Array.from(propsByName.values()).map(({ name, platforms, searchTerms }) => ({
    kitLabel: kit.label,
    label: `${name} (${kit.label})`,
    path: kit.value,
    platforms: Array.from(platforms),
    propName: name,
    searchTerms: Array.from(searchTerms),
    type: 'prop',
    value: `${kit.value}#${name}`,
  }))
}

const combineKitsandVisualGuidelines = (
  kits: Kit[],
  global_props_and_tokens?: Record<string, any>,
): Kit[] => {
  const kitItems = kits.map((kit) => ({ ...kit, type: kit.type ?? 'kit' }))
  const propItems = kitItems.flatMap(kitPropItems)

  const globalPropsItems = global_props_and_tokens?.global_props?.map((item: string) => ({
    label: item.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase()),
    value: `/global_props/${item}`,
    type: 'global_prop'
  })) || []

  const tokensItems = global_props_and_tokens?.tokens?.map((item: string) => ({
    label: item.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase()),
    value: `/tokens/${item}`,
    type: 'token'
  })) || []

  const GLOBAL_EVENT_PROP_LABELS: Record<string, string> = {
    on_click: 'onClick',
  }

  const globalEventPropsItems = global_props_and_tokens?.global_event_props?.map((item: string) => ({
    label:
      GLOBAL_EVENT_PROP_LABELS[item] ||
      item.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase()),
    value: `/global_event_props/${item}`,
    type: 'global_event_prop'
  })) || []
  
  const items = [...kitItems, ...globalPropsItems, ...globalEventPropsItems, ...tokensItems, ...propItems]
    .sort((a, b) => a.label.localeCompare(b.label))

  return sortBySearchGroup(items)
}

const normalizePathForPlatform = (path: string, platform: string) => {
  if (!path || !path.startsWith('/')) return path

  if (
    path.startsWith('/global_props/') ||
    path.startsWith('/global_event_props/') ||
    path.startsWith('/tokens/')
  ) {
    return path
  }

  if (path.startsWith('/kit_category/')) {
    const [pathname, rawQuery = ''] = path.split('?')
    const params = new URLSearchParams(rawQuery)
    params.set('type', platform)
    return `${pathname}?${params.toString()}`
  }

  if (path.startsWith('/kits/')) {
    if (/\/(react|rails)$/.test(path)) {
      return path.replace(/\/(react|rails)$/, `/${platform}`)
    }

    return `${path}/${platform}`
  }

  return path
}

const KitSearch = ({ classname, id, kits, platform = 'react', global_props_and_tokens, marginBottom, onNavigate, searchResetKey }: KitSearchProps) => {
  const kitsAndGuidelines = useMemo(
    () => combineKitsandVisualGuidelines(kits, global_props_and_tokens),
    [kits, global_props_and_tokens],
  )
  const { darkMode } = useDarkMode()
  const [query, setQuery] = useState('')
  const [selectionReset, setSelectionReset] = useState(0)
  const filteredKits = useMemo(() => {
    if (!query) return kitsAndGuidelines

    // kit.schema.json stores camelCase. Rails queries use snake_case.
    // Drop the same separators normalizePropName ignores so both match.
    const searchableQuery = stripPropSeparators(query) || query

    return sortBySearchGroup(searchItems(kitsAndGuidelines, searchableQuery, ['label', normalizedSearchTerms]))
  }, [kitsAndGuidelines, query])

  useEffect(() => {
    if (id === 'desktop-kit-search') {
      window.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.key === 'k') {
          const kitSearch = document.querySelector('#desktop-kit-search #react-select-2-input') as HTMLInputElement
          kitSearch === document.activeElement ? kitSearch.blur() : kitSearch.focus()
        }
      })
    }
  }, [ id ])

  const handleChange = (selection: Kit | null) => {
    if (!selection) return

    const selectedPlatform = selection.type === 'prop' && selection.platforms?.length === 1
      ? selection.platforms[0]
      : platform
    const nextPath = normalizePathForPlatform(selection.path ?? selection.value, selectedPlatform)

    setQuery('')
    setSelectionReset((count) => count + 1)

    if (onNavigate) {
      onNavigate(nextPath)
    } else {
      window.location.href = nextPath
    }
  }

  const handleFilteredKits = (query: string) => {
    setQuery(query)
  }

  const SEARCH_TYPE_BADGES: Record<string, string> = {
    kit: 'Component',
    global_prop: 'Global Prop',
    global_event_prop: 'Global Event Prop',
    token: 'Token',
  }

  const Item = ({ caption, labelLeft, platforms = [], type }: { caption?: string, labelLeft: string, platforms?: string[], type: string }) => (
    <Flex alignItems="center" justify="between">
        {caption ? (
          <div>
            <Body
                dark={darkMode}
                text={labelLeft}
            />
            <Caption
                dark={darkMode}
                text={caption}
                size="xs"
            />
          </div>
        ) : labelLeft}
        <Pill
          dark={darkMode}
          margin="xs"
          size="sm"
          textTransform="none"
          variant="primary"
        >
          {type === 'prop' ? (
            <Flex alignItems="center" justify="center">
              <Title
              className="pb_pill_text"
              size={4}
              tag="div"
              text="Kit Prop"
              marginRight="xxs"
            />
            <PlatformMarks platforms={platforms} />
            </Flex>
          ) : (
            <Title
              className="pb_pill_text"
              size={4}
              tag="div"
              text={SEARCH_TYPE_BADGES[type]}
            />
          )}
        </Pill>
    </Flex>
  )

  return (
      <Typeahead
        key={`${id}__${searchResetKey ?? ''}__${selectionReset}`}
        className={classname}
        dark={darkMode}
        filterOption={() => true}
        id={id}
        marginBottom={marginBottom || 'sm'}
        onChange={handleChange}
        onInputChange={handleFilteredKits}
        options={filteredKits}
        placeholder="Search..."
        valueComponent={(option: Kit) => {
          if (option.type === 'prop') {
            return <Item caption={option.kitLabel} labelLeft={option.propName ?? option.label} platforms={option.platforms} type={option.type} />
          }
          if (option.type && SEARCH_TYPE_BADGES[option.type]) {
            return <Item labelLeft={option.label} platforms={option.platforms} type={option.type} />
          }
          return <>{option.label}</>
        }}
      />
  )
}

export default KitSearch
